import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface VendorAccess {
  id: string;
  vendorName: string;
  accessCode: string;
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
  _count: { questions: number };
}

interface VendorQuestion {
  id: string;
  vendorAccessId: string;
  scope: 'General' | 'Section' | 'Article';
  scopeId: string | null;
  question: string;
  attachmentUrl: string | null;
  attachmentName: string | null;
  response: string | null;
  responseAttachmentUrl: string | null;
  responseAttachmentName: string | null;
  status: 'Open' | 'Answered' | 'Resolved';
  createdAt: string;
  vendorAccess?: { vendorName: string };
}

export function VendorAccessTab() {
  const [accessCodes, setAccessCodes] = useState<VendorAccess[]>([]);
  const [questions, setQuestions] = useState<VendorQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [newExpiry, setNewExpiry] = useState('');

  // Response form
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');

  // Clipboard feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [codes, qs] = await Promise.all([
        apiClient<VendorAccess[]>('/vendor-portal/admin/access-codes'),
        apiClient<VendorQuestion[]>('/vendor-portal/admin/questions'),
      ]);
      setAccessCodes(codes);
      setQuestions(qs);
    } catch { setError('Failed to load vendor access data.'); }
    finally { setLoading(false); }
  }

  async function handleCreate() {
    if (!newVendorName.trim()) return;
    try {
      await apiClient('/vendor-portal/admin/access-codes', {
        method: 'POST',
        body: { vendorName: newVendorName.trim(), expiresAt: newExpiry || undefined },
      });
      setNewVendorName('');
      setNewExpiry('');
      setShowCreate(false);
      fetchData();
    } catch { setError('Failed to create access code.'); }
  }

  async function handleRevoke(id: string) {
    try {
      await apiClient(`/vendor-portal/admin/access-codes/${id}/revoke`, { method: 'PATCH' });
      fetchData();
    } catch { setError('Failed to revoke access code.'); }
  }

  async function handleReactivate(id: string) {
    try {
      await apiClient(`/vendor-portal/admin/access-codes/${id}/reactivate`, { method: 'PATCH' });
      fetchData();
    } catch { setError('Failed to reactivate access code.'); }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this access code and all its associated questions?')) return;
    try {
      await apiClient(`/vendor-portal/admin/access-codes/${id}`, { method: 'DELETE' });
      fetchData();
    } catch { setError('Failed to delete access code.'); }
  }

  async function handleRespond(questionId: string) {
    if (!responseText.trim()) return;
    try {
      await apiClient(`/vendor-portal/questions/${questionId}/respond`, {
        method: 'PATCH',
        body: { response: responseText.trim() },
      });
      setRespondingTo(null);
      setResponseText('');
      fetchData();
    } catch { setError('Failed to submit response.'); }
  }

  async function handleResolve(questionId: string) {
    try {
      await apiClient(`/vendor-portal/questions/${questionId}/resolve`, { method: 'PATCH' });
      fetchData();
    } catch { setError('Failed to resolve question.'); }
  }

  function copyToClipboard(text: string, id: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }

  if (loading) return <div style={styles.emptyState}>Loading vendor access data...</div>;

  const openQuestions = questions.filter((q) => q.status === 'Open');
  const answeredQuestions = questions.filter((q) => q.status !== 'Open');

  return (
    <div style={styles.container}>
      {/* Access Codes Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>🔐 Vendor Access Codes</h3>
          <button style={styles.primaryBtn} onClick={() => setShowCreate(true)}>+ Generate Code</button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {showCreate && (
          <div style={styles.createForm}>
            <input
              style={styles.input}
              placeholder="Vendor name (e.g. PrintPro Inc.)"
              value={newVendorName}
              onChange={(e) => setNewVendorName(e.target.value)}
              autoFocus
            />
            <input
              type="date"
              style={{ ...styles.input, maxWidth: '180px' }}
              value={newExpiry}
              onChange={(e) => setNewExpiry(e.target.value)}
              title="Expiry date (optional) — code works until end of this day"
              placeholder="No expiry"
            />
            <button style={styles.smallBtn} onClick={handleCreate}>Create</button>
            <button style={styles.secondaryBtn} onClick={() => { setShowCreate(false); setNewVendorName(''); setNewExpiry(''); }}>Cancel</button>
          </div>
        )}

        {accessCodes.length === 0 ? (
          <div style={styles.emptyState}>No vendor access codes generated yet.</div>
        ) : (
          <div style={styles.codeList}>
            {accessCodes.map((code) => (
              <div key={code.id} style={styles.codeCard}>
                <div style={styles.codeCardTop}>
                  <div>
                    <span style={styles.vendorName}>{code.vendorName}</span>
                    <span style={{ ...styles.statusPill, backgroundColor: code.isActive ? '#dcfce7' : '#fee2e2', color: code.isActive ? '#16a34a' : '#dc2626' }}>
                      {code.isActive ? 'Active' : 'Revoked'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    {code.isActive ? (
                      <button style={styles.iconBtn} onClick={() => handleRevoke(code.id)} title="Revoke" aria-label={`Revoke ${code.vendorName}`}>🚫</button>
                    ) : (
                      <button style={styles.iconBtn} onClick={() => handleReactivate(code.id)} title="Reactivate" aria-label={`Reactivate ${code.vendorName}`}>✅</button>
                    )}
                    <button style={styles.iconBtn} onClick={() => handleDelete(code.id)} title="Delete" aria-label={`Delete ${code.vendorName}`}>🗑️</button>
                  </div>
                </div>
                <div style={styles.codeRow}>
                  <span style={styles.codeLabel}>Access Code:</span>
                  <code style={styles.codeValue}>{code.accessCode}</code>
                  <button
                    style={styles.copyBtn}
                    onClick={() => copyToClipboard(code.accessCode, code.id)}
                    title="Copy to clipboard"
                    aria-label="Copy access code"
                  >
                    {copiedId === code.id ? '✓' : '📋'}
                  </button>
                </div>
                <div style={styles.codeMeta}>
                  <span>Created: {new Date(code.createdAt).toLocaleDateString()}</span>
                  {code.expiresAt && <span>Expires: {new Date(code.expiresAt).toLocaleDateString()}</span>}
                  <span>Questions: {code._count.questions}</span>
                </div>
                <div style={styles.portalLink}>
                  Portal URL: <a href="/vendor-portal" target="_blank" rel="noopener noreferrer" style={{ color: '#1a6b3c', fontSize: '0.75rem' }}>{window.location.origin}/vendor-portal</a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vendor Questions Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>❓ Vendor Questions ({openQuestions.length} open)</h3>

        {openQuestions.length === 0 && answeredQuestions.length === 0 ? (
          <div style={styles.emptyState}>No vendor questions submitted yet.</div>
        ) : (
          <>
            {openQuestions.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '0.85rem', color: '#d97706', marginBottom: '0.5rem' }}>Open Questions</h4>
                {openQuestions.map((q) => (
                  <div key={q.id} style={styles.questionCard}>
                    <div style={styles.questionHeader}>
                      <span style={styles.scopeBadge}>{q.scope}{q.vendorAccess ? ` • ${q.vendorAccess.vendorName}` : ''}</span>
                      <span style={styles.questionDate}>{new Date(q.createdAt).toLocaleDateString()}</span>
                    </div>
                    <p style={styles.questionText}>{q.question}</p>
                    {q.attachmentName && (
                      <a href={q.attachmentUrl || '#'} target="_blank" rel="noopener noreferrer" style={styles.attachLink}>📎 {q.attachmentName}</a>
                    )}
                    {respondingTo === q.id ? (
                      <div style={styles.respondForm}>
                        <textarea
                          style={styles.textarea}
                          placeholder="Type your response..."
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          rows={2}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <button style={styles.smallBtn} onClick={() => handleRespond(q.id)}>Send Response</button>
                          <button style={styles.secondaryBtn} onClick={() => { setRespondingTo(null); setResponseText(''); }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginTop: '0.5rem' }}>
                        <button style={styles.smallBtn} onClick={() => { setRespondingTo(q.id); setResponseText(''); }}>Reply</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {answeredQuestions.length > 0 && (
              <div>
                <h4 style={{ fontSize: '0.85rem', color: '#16a34a', marginBottom: '0.5rem' }}>Answered / Resolved ({answeredQuestions.length})</h4>
                {answeredQuestions.map((q) => (
                  <div key={q.id} style={{ ...styles.questionCard, opacity: q.status === 'Resolved' ? 0.7 : 1 }}>
                    <div style={styles.questionHeader}>
                      <span style={styles.scopeBadge}>{q.scope}{q.vendorAccess ? ` • ${q.vendorAccess.vendorName}` : ''}</span>
                      <span style={{ ...styles.qStatusBadge, backgroundColor: q.status === 'Answered' ? '#dcfce7' : '#f3f4f6', color: q.status === 'Answered' ? '#16a34a' : '#6b7280' }}>
                        {q.status}
                      </span>
                    </div>
                    <p style={styles.questionText}>{q.question}</p>
                    {q.response && (
                      <div style={styles.responseBox}>
                        <strong style={{ fontSize: '0.75rem', color: '#0f2d1f' }}>Your response:</strong>
                        <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: '#333' }}>{q.response}</p>
                      </div>
                    )}
                    {q.status === 'Answered' && (
                      <button style={{ ...styles.secondaryBtn, marginTop: '0.5rem', fontSize: '0.7rem' }} onClick={() => handleResolve(q.id)}>Mark Resolved</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '0' },
  section: { marginBottom: '2rem' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#0f2d1f', margin: 0 },
  primaryBtn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  smallBtn: { padding: '0.3rem 0.6rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { padding: '0.3rem 0.6rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1 },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' },
  emptyState: { padding: '2rem', textAlign: 'center' as const, color: '#666', fontSize: '0.9rem' },
  createForm: { display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', marginBottom: '1rem', flexWrap: 'wrap' as const },
  input: { padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem', flex: 1, minWidth: '180px' },

  // Code cards
  codeList: { display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' },
  codeCard: { padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fff' },
  codeCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  vendorName: { fontWeight: 600, color: '#0f2d1f', fontSize: '0.95rem', marginRight: '0.5rem' },
  statusPill: { padding: '0.1rem 0.4rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600 },
  codeRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' },
  codeLabel: { fontSize: '0.75rem', color: '#666', fontWeight: 500 },
  codeValue: { fontSize: '0.75rem', color: '#333', backgroundColor: '#f3f4f6', padding: '0.2rem 0.4rem', borderRadius: '3px', fontFamily: 'monospace', wordBreak: 'break-all' as const },
  copyBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', padding: '2px 4px' },
  codeMeta: { display: 'flex', gap: '1rem', fontSize: '0.7rem', color: '#999' },
  portalLink: { marginTop: '0.5rem', fontSize: '0.7rem', color: '#666' },

  // Questions
  questionCard: { padding: '0.75rem', marginBottom: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '6px', backgroundColor: '#fff' },
  questionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' },
  scopeBadge: { fontSize: '0.7rem', color: '#666', background: '#f3f4f6', padding: '0.1rem 0.4rem', borderRadius: '3px' },
  questionDate: { fontSize: '0.7rem', color: '#999' },
  questionText: { margin: '0.3rem 0', fontSize: '0.85rem', color: '#333' },
  attachLink: { fontSize: '0.75rem', color: '#1a6b3c', textDecoration: 'none' },
  qStatusBadge: { padding: '0.1rem 0.4rem', borderRadius: '3px', fontSize: '0.65rem', fontWeight: 600 },
  respondForm: { marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f9fafb', borderRadius: '6px' },
  textarea: { width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical' as const, boxSizing: 'border-box' as const },
  responseBox: { marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f0fdf4', borderRadius: '4px', border: '1px solid #d1fae5' },
};
